import React from 'react';
import {
  User, Truck, Calendar, AlertTriangle, Briefcase, Award, Sparkles, Mail,
  Building2, TrendingUp, CheckCircle2, Star,
} from 'lucide-react';
import HSTLogo from '../HSTLogo';
import { formatMoney } from '../../utils/format';
import { useTranslation } from '../../i18n';

const FRAME =
  'rounded-2xl border border-ink-600 bg-card/95 backdrop-blur-md shadow-2xl px-6 py-5 max-w-md mx-auto animate-in fade-in slide-in-from-bottom-3 duration-500';

export default function DemoSceneRenderer({ scene }) {
  if (!scene) return null;
  switch (scene.kind) {
    case 'intro':       return <IntroScene />;
    case 'hire':        return <HireScene driver={scene.visual.driver} />;
    case 'contract':    return <ContractScene contract={scene.visual.contract} />;
    case 'fastForward': return <FastForwardScene visual={scene.visual} />;
    case 'event':       return <EventScene event={scene.visual.event} />;
    case 'capex':       return <CapexScene capex={scene.visual.capex} />;
    case 'support':     return <SupportScene support={scene.visual.support} />;
    case 'achievement': return <AchievementScene achievement={scene.visual.achievement} />;
    case 'recap':       return <RecapScene featureKeys={scene.visual.featureKeys} />;
    case 'contact':     return null;
    default:            return null;
  }
}

function IntroScene() {
  const { t } = useTranslation();
  return (
    <div className={`${FRAME} text-center`} data-testid="demo-scene-intro">
      <div className="flex justify-center mb-3">
        <HSTLogo size={64} withGlow />
      </div>
      <h2 className="font-display text-3xl font-black text-foreground tracking-tight">HST</h2>
      <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-mono mt-2">
        {t('hst_subtitle')}
      </p>
      <p className="text-sm text-slate-300 mt-4 leading-relaxed">
        {t('demo_subtitle_hero')}
      </p>
    </div>
  );
}

function HireScene({ driver }) {
  const { t } = useTranslation();
  return (
    <div className={FRAME} data-testid="demo-scene-hire">
      <SceneHeader icon={User} label={t('demo_scene_header_recruitment')} tone="accent" />
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-accent/20 border-2 border-accent flex items-center justify-center font-bold text-accent">
          SM
        </div>
        <div className="flex-1">
          <p className="font-bold text-slate-100">{driver.name}</p>
          <p className="text-xs text-slate-500">{t(driver.experienceKey)} · {t(driver.specialtyKey)}</p>
          <div className="flex items-center gap-0.5 mt-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className={`w-3 h-3 ${i <= driver.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}`} />
            ))}
          </div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
        <KpiTile label={t('demo_scene_hire_monthly_salary')} value={`${formatMoney(driver.cost)} €`} tone="danger" />
        <KpiTile label={t('demo_scene_hire_recruit')} value={t('demo_scene_hire_signing')} tone="emerald" />
      </div>
    </div>
  );
}

function ContractScene({ contract }) {
  const { t } = useTranslation();
  return (
    <div className={FRAME} data-testid="demo-scene-contract">
      <SceneHeader icon={Briefcase} label={t('demo_scene_header_contract')} tone="amber" />
      <p className="font-bold text-slate-100 text-base">{contract.client}</p>
      <p className="text-xs text-slate-500 mt-0.5">{t(contract.kindKey)} · {contract.kmTotal} km · {contract.days} {t('cc_save_day_label')}s</p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <KpiTile label={t('demo_scene_contract_daily_rate')} value={`${formatMoney(contract.dailyRate)} €`} tone="accent" />
        <KpiTile label={t('demo_scene_contract_total_ht')} value={`${formatMoney(contract.totalRevenue)} €`} tone="emerald" />
      </div>
    </div>
  );
}

function FastForwardScene({ visual }) {
  const { t } = useTranslation();
  return (
    <div className={FRAME} data-testid="demo-scene-fast-forward">
      <SceneHeader icon={Calendar} label={t('demo_scene_header_time')} tone="accent" />
      <div className="flex items-center justify-center gap-2 my-3">
        {[...Array(visual.daysToAdvance)].map((_, i) => (
          <div
            // Stable synthetic key — the list never reorders (each index
            // permanently maps to "day i+1"), so index-as-key is safe
            // here per React's own guidance.
            key={`day-plus-${i + 1}`}
            className="w-10 h-10 rounded-md bg-money/15 border border-money/40 flex items-center justify-center text-money font-mono font-bold animate-pulse"
            style={{ animationDelay: `${i * 200}ms` }}
          >
            J+{i + 1}
          </div>
        ))}
      </div>
      <p className="text-center text-sm text-slate-400">
        {t('demo_ff_subtitle')} <span className="text-money font-bold">+{formatMoney(visual.dailyNet)} {t('demo_ff_per_day')}</span>
      </p>
    </div>
  );
}

function EventScene({ event }) {
  const { t } = useTranslation();
  return (
    <div className={`${FRAME} border-warn/40`} data-testid="demo-scene-event">
      <SceneHeader icon={AlertTriangle} label={t('demo_scene_header_event')} tone="warn" />
      <p className="font-bold text-slate-100">{t(event.titleKey)}</p>
      <p className="text-xs text-slate-500 mt-0.5">{t(event.impactKey)}</p>
      <div className="mt-3 grid grid-cols-1 gap-2">
        <KpiTile label={t('demo_scene_event_cost')} value={`${formatMoney(event.cost)} €`} tone="danger" />
      </div>
    </div>
  );
}

function CapexScene({ capex }) {
  const { t } = useTranslation();
  return (
    <div className={FRAME} data-testid="demo-scene-capex">
      <SceneHeader icon={Truck} label={t('demo_scene_header_capex')} tone="purple" />
      <p className="font-bold text-slate-100">{t(capex.vehicleKey)}</p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <KpiTile label={t('demo_scene_capex_price')} value={`${formatMoney(capex.price)} €`} tone="accent" />
        <KpiTile label={t('demo_scene_capex_annual_is_saved')} value={`${formatMoney(capex.annualISsaved)} €`} tone="emerald" />
        <KpiTile label={t('demo_scene_capex_total_is_saved')} value={`${formatMoney(capex.totalISsaved)} €`} tone="emerald" />
        <KpiTile label={t('demo_scene_capex_net_cost')} value={`${formatMoney(capex.netCost)} €`} tone="purple" />
      </div>
    </div>
  );
}

function SupportScene({ support }) {
  const { t } = useTranslation();
  return (
    <div className={FRAME} data-testid="demo-scene-support">
      <SceneHeader icon={Building2} label={t('demo_scene_header_support')} tone="emerald" />
      <p className="font-bold text-slate-100">{support.name}</p>
      <p className="text-xs text-slate-500 mt-0.5">{t(support.roleKey)}</p>
      <ul className="mt-3 space-y-1">
        {support.unlockKeys.map((k) => (
          <li key={k} className="text-[11px] text-emerald-300 flex items-center gap-1.5">
            <CheckCircle2 className="w-3 h-3" /> {t(k)}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[10px] uppercase tracking-wider text-slate-500 font-mono">
        {t('demo_scene_support_monthly_cost')} · <span className="text-danger font-bold">{formatMoney(support.monthlyCost)} €</span>
      </p>
    </div>
  );
}

function AchievementScene({ achievement }) {
  const { t } = useTranslation();
  return (
    <div className={`${FRAME} border-amber-400/60 bg-gradient-to-b from-amber-500/10 to-card/95`} data-testid="demo-scene-achievement">
      <div className="flex justify-center mb-3">
        <div className="relative">
          <Award className="w-20 h-20 text-amber-400 drop-shadow-lg" />
          <Sparkles className="w-6 h-6 text-amber-200 absolute -top-1 -right-1 animate-pulse" />
        </div>
      </div>
      <p className="text-center text-[10px] uppercase tracking-[0.3em] text-amber-300 font-mono">
        {t('demo_scene_ach_unlocked')}
      </p>
      <p className="text-center font-bold text-lg text-amber-100 mt-1">{t(achievement.titleKey)}</p>
      <p className="text-center text-xs text-slate-400 mt-1">{t(achievement.descriptionKey)}</p>
      <p className="text-center mt-3">
        <span className="inline-block px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 text-xs font-bold font-mono">
          +{achievement.xp} XP
        </span>
      </p>
    </div>
  );
}

function RecapScene({ featureKeys }) {
  const { t } = useTranslation();
  return (
    <div className={FRAME} data-testid="demo-scene-recap">
      <SceneHeader icon={TrendingUp} label={t('demo_scene_recap_intro')} tone="accent" />
      <div className="grid grid-cols-2 gap-2 mt-3">
        {featureKeys.map((k, i) => (
          <div
            key={k}
            className="text-[11px] text-slate-300 px-2 py-1.5 rounded bg-ink-700/40 border border-ink-600 leading-tight animate-in fade-in slide-in-from-left-2"
            style={{ animationDelay: `${i * 120}ms`, animationDuration: '400ms' }}
          >
            {t(k)}
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 italic mt-3 text-center">
        {t('demo_scene_recap_outro')}
      </p>
    </div>
  );
}

function SceneHeader({ icon: Icon, label, tone }) {
  const toneMap = {
    accent: 'text-accent border-accent/40 bg-accent/10',
    amber: 'text-amber-300 border-amber-400/40 bg-amber-500/10',
    warn: 'text-warn border-warn/40 bg-warn/10',
    purple: 'text-purple-300 border-purple-400/40 bg-purple-500/10',
    emerald: 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10',
  };
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-wider font-mono font-bold mb-3 ${toneMap[tone] || toneMap.accent}`}>
      <Icon className="w-3 h-3" /> {label}
    </div>
  );
}

function KpiTile({ label, value, tone }) {
  const toneMap = {
    accent: 'text-accent',
    danger: 'text-danger',
    emerald: 'text-emerald-300',
    purple: 'text-purple-300',
  };
  return (
    <div className="px-3 py-2 rounded-md bg-ink-700/40 border border-ink-600">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{label}</p>
      <p className={`text-base font-bold font-mono ${toneMap[tone] || 'text-foreground'}`}>{value}</p>
    </div>
  );
}

export { Mail };
